import React, { useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { SessionsFamily, StaffPeriods } from '../types';
import { formatDecimal, formatInt, monthLabel, periodLabel } from '../utils/metrics';
import { ChartLegend } from './ChartLegend';
import { CategoryFilterProps, ServiceCategorySelect } from './ServiceCategorySelect';

export const LATEST_COLOR = '#2563EB';
export const PRIOR_COLOR = '#9CA3AF';
const MEAN_COLOR = '#374151';

export interface SessionsChartProps {
  sessions: SessionsFamily;
  periods: StaffPeriods;
  /** Service-category multi-select shown beside the legend; `sessions` is already restricted to the selection. */
  categoryFilter?: CategoryFilterProps;
}

interface Point {
  label: string;
  count: number;
  period: 'latest_6m' | 'prior_6m';
}

const plain = (value: number | null): string => (value === null ? 'n/a' : Number.isInteger(value) ? formatInt(value) : formatDecimal(value));

const SessionsTooltip: React.FC<{ active?: boolean; payload?: Array<{ payload: Point }> }> = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <Paper elevation={6} sx={{ p: 1.5, borderRadius: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {point.label}
      </Typography>
      <Typography variant="body2">{formatInt(point.count)} sessions</Typography>
      <Typography variant="caption" color="text.secondary">
        {point.period === 'latest_6m' ? 'Latest 6 months' : 'Prior 6 months'}
      </Typography>
    </Paper>
  );
};

/** 12-month bar chart (prior grey, latest blue, mean per period) with its period table. */
export const SessionsChart: React.FC<SessionsChartProps> = ({ sessions, periods, categoryFilter }) => {
  const data = useMemo<Point[]>(
    () => sessions.monthly.map((m) => ({ label: monthLabel(m.month), count: m.count, period: m.period })),
    [sessions.monthly]
  );
  const latest = data.filter((d) => d.period === 'latest_6m');
  const prior = data.filter((d) => d.period === 'prior_6m');

  // The mean values live in the legend and the table; an inline label would sit on the bars.
  const meanLine = (points: Point[], mean: number | null) =>
    points.length > 0 && mean !== null ? (
      <ReferenceLine
        segment={[
          { x: points[0].label, y: mean },
          { x: points[points.length - 1].label, y: mean },
        ]}
        stroke={MEAN_COLOR}
        strokeWidth={2}
        ifOverflow="extendDomain"
      />
    ) : null;
  const meanKey = `Period mean · prior ${plain(sessions.prior.mean)} · latest ${plain(sessions.latest.mean)}`;

  return (
    <Card component="section" aria-label="Sessions per month">
      <CardHeader
        title="Sessions per month"
        subheader="Unique sessions you coached, excluding no-shows and late cancellations"
        sx={{ pb: 1 }}
      />
      <Box sx={{ px: 2.5, pb: 1.5, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
        <ChartLegend
          keys={[
            { label: 'Latest 6 months', color: LATEST_COLOR },
            { label: 'Prior 6 months', color: PRIOR_COLOR },
            { label: meanKey, color: MEAN_COLOR, kind: 'line' },
          ]}
        />
        {categoryFilter && (categoryFilter.options.length > 0 || categoryFilter.selected !== null) ? (
          <ServiceCategorySelect {...categoryFilter} />
        ) : null}
      </Box>
      <CardContent sx={{ pt: 0 }}>
        <Box sx={{ width: '100%', height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 24, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="#E5E7EB" />
              {latest.length > 0 ? (
                <ReferenceArea x1={latest[0].label} x2={latest[latest.length - 1].label} fill={LATEST_COLOR} fillOpacity={0.04} />
              ) : null}
              <XAxis dataKey="label" interval={0} tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={{ stroke: '#D1D5DB' }} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: '#6B7280' }} axisLine={false} tickLine={false} width={36} allowDecimals={false} />
              <Tooltip content={<SessionsTooltip />} cursor={{ fill: 'rgba(37, 99, 235, 0.08)' }} />
              {meanLine(prior, sessions.prior.mean)}
              {meanLine(latest, sessions.latest.mean)}
              <Bar dataKey="count" maxBarSize={24} radius={[4, 4, 0, 0]} isAnimationActive={false}>
                {data.map((point) => (
                  <Cell key={point.label} fill={point.period === 'latest_6m' ? LATEST_COLOR : PRIOR_COLOR} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>

        <Table size="small" aria-label="Sessions by period" sx={{ mt: 2, '& td, & th': { fontVariantNumeric: 'tabular-nums' } }}>
          <TableHead>
            <TableRow>
              <TableCell>Period</TableCell>
              <TableCell align="right">Sessions</TableCell>
              <TableCell align="right">Active months</TableCell>
              <TableCell align="right">Mean / month</TableCell>
              <TableCell align="right">Median</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Latest · {periodLabel(periods.latest)}</TableCell>
              <TableCell align="right">{formatInt(sessions.latest.total)}</TableCell>
              <TableCell align="right">{sessions.latest.activeMonths}</TableCell>
              <TableCell align="right">{plain(sessions.latest.mean)}</TableCell>
              <TableCell align="right">{plain(sessions.latest.median)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ color: 'text.secondary' }}>Prior · {periodLabel(periods.prior)}</TableCell>
              <TableCell align="right">{formatInt(sessions.prior.total)}</TableCell>
              <TableCell align="right">{sessions.prior.activeMonths}</TableCell>
              <TableCell align="right">{plain(sessions.prior.mean)}</TableCell>
              <TableCell align="right">{plain(sessions.prior.median)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
